/** Merchant domain types for the df-portal. */
import { z } from 'zod';

/** How the platform collects merchant receivables (F10 §2.6). */
export type CollectionMode = 'manual' | 'assisted' | 'auto';

/** platform-service `Merchant.status`; the review states come from shop self-onboarding. */
export const MerchantStatusSchema = z.enum([
  'active',
  'suspended',
  'pending_review',
  'needs_change',
  'not_approved',
]);
export type MerchantStatus = z.infer<typeof MerchantStatusSchema>;

export const isMerchantStatus = (value: unknown): value is MerchantStatus =>
  MerchantStatusSchema.safeParse(value).success;

/** Unrecognised or missing status maps to null so it never renders as active. */
export const toMerchantStatus = (value: unknown): MerchantStatus | null =>
  isMerchantStatus(value) ? value : null;

/** Which onboarding step the owner is sent back to on `request_change`. */
export const MerchantReviewFieldSchema = z.enum(['shopName', 'location', 'payout']);
export type MerchantReviewField = z.infer<typeof MerchantReviewFieldSchema>;

export const MerchantReviewDecisionSchema = z.enum([
  'activate',
  'request_change',
  'decline',
]);
export type MerchantReviewDecision = z.infer<typeof MerchantReviewDecisionSchema>;

const reviewNote = z.string().trim().min(1);

/** Body of POST /v1/merchants/{id}/review. */
export const MerchantReviewSchema = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('activate') }),
  z.object({
    decision: z.literal('request_change'),
    note: reviewNote,
    field: MerchantReviewFieldSchema,
  }),
  z.object({ decision: z.literal('decline'), note: reviewNote }),
]);
export type MerchantReviewModel = z.infer<typeof MerchantReviewSchema>;

export interface MerchantReviewDraft {
  decision: MerchantReviewDecision;
  note?: string;
  field?: MerchantReviewField | null;
}

/** Null when the draft isn't submittable (missing note/field). */
export const buildMerchantReviewBody = (
  draft: MerchantReviewDraft
): MerchantReviewModel | null => {
  const parsed = MerchantReviewSchema.safeParse(
    draft.decision === 'activate' ? { decision: 'activate' } : draft
  );
  return parsed.success ? parsed.data : null;
};

export const canActivateMerchant = (status: MerchantStatus | null) =>
  status === 'pending_review' || status === 'needs_change';

export const canRequestChangeOrDecline = (status: MerchantStatus | null) =>
  status === 'pending_review';

export interface MerchantListItem {
  id: string;
  businessName: string;
  ownerPhone: string;
  collectionMode: string;
  status: MerchantStatus | null;
  createdAt: string;
}

export interface MerchantDetail extends MerchantListItem {
  tenantId: string;
  /** Channel → settlement account number, e.g. { zaad: "2526..." } (F10 §6.2). */
  settlementAccounts: Record<string, string>;
  lastModifiedAt: string | null;
  reviewNote: string | null;
  reviewField: MerchantReviewField | null;
  reviewedAt: string | null;
}

/** Role a staff member holds at a merchant (platform-service `MerchantStaffCreate`). */
export type MerchantStaffRole = 'owner' | 'staff';

/**
 * A `MerchantStaff` link row as returned by platform-service.
 *
 * NOTE: the backend's `MerchantStaffResponse` only exposes the internal
 * `consumerSeqId` — it does NOT join in the consumer's phone or name. The
 * `consumerPhone` / `consumerName` fields are therefore usually empty; they are
 * kept (and mapped tolerantly) so the UI picks them up for free if platform-service
 * ever enriches the payload.
 */
export interface MerchantStaffItem {
  id: string;
  consumerSeqId: number | null;
  consumerPhone: string;
  consumerName: string;
  role: string;
  isActive: boolean;
  createdAt: string;
}

/** Payload for POST /v1/merchants/{id}/staff — resolves the consumer BY PHONE. */
export interface AddMerchantStaffModel {
  /**
   * Must match `Consumer.phone` byte-exactly — the consumer row is only created
   * at the consumer's first mobile OTP login, and phone formatting differs per
   * tenant (nuur stores bare `6…`, mazeedpay stores `+2526…`).
   */
  consumerPhone: string;
  role?: MerchantStaffRole;
}

export interface UpdateMerchantModel extends Partial<CreateMerchantModel> {
  status?: MerchantStatus;
}

export interface CreateMerchantModel {
  businessName: string;
  ownerPhone: string;
  ownerName?: string;
  /** Channel → settlement account number, e.g. { zaad: "2526..." } (F10 §6.2). */
  settlementAccounts?: Record<string, string>;
  /** Collection mode — how repayments are collected (F10 §2.6). */
  collectionMode?: CollectionMode;
  address?: string;
  city?: string;
  region?: string;
  category?: string;
}

/** CreditAccount as returned by loan-service merchant surface. */
export interface CreditAccountItem {
  id: string;
  tenantId: string;
  merchantId: string;
  merchantName?: string;
  customerMsisdn: string;
  currency: string;
  creditLimit: number;
  runningBalance: number;
  status: string;
  daysOutstanding?: number | null;
  overdue?: boolean;
  createdAt: string;
}

export interface Installment {
  n: number;
  due: string;
  amt: number;
}

export interface ChargeItem {
  id: string;
  creditAccountId: string;
  merchantRef: string | null;
  currency: string;
  total: number;
  acknowledgment: string;
  note: string | null;
  planType: string;
  installmentPlan: Installment[] | null;
  firstDueDate: string | null;
  createdAt: string;
}
